import { PasswordInput } from "@/components/ui/PasswordInput";
import {
  Box,
  Button,
  Card,
  ClientOnly,
  Heading,
  Input,
  Skeleton,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useNavigate, useSearchParams } from "react-router-dom";
import ColorModeToggle from "@/components/color-mode-toggle";
import { useLoginMutation } from "@/store/authApi";
import { useFormErrorHandler } from "@/hooks/useFormErrorHandler";
import { FormErrorDisplay, RootErrorDisplay } from "@/components/ui/FormErrorDisplay";
import Logo from "@/assets/logo.svg";
import {
  getPostAuthRedirectPath,
  sanitizeNextPath,
} from "@/utils/socialAuth";
import SocialAuthOptions from "@/components/auth/SocialAuthOptions";

// Schema definition
const loginSchema = yup.object({
  email: yup
    .string()
    .required('Email is required')
    .email('Please enter a valid email address'),
  password: yup
    .string()
    .required('Password is required')
    .min(6, 'Password must be at least 6 characters long')
});

type LoginFormData = yup.InferType<typeof loginSchema>;

export default function LoginPage() {
  const {
    handleSubmit,
    control,
    setError,
    clearErrors,
    formState: { errors, isSubmitting }
  } = useForm<LoginFormData>({
    resolver: yupResolver(loginSchema),
    defaultValues: {
      email: "",
      password: ""
    }
  });
  
  const { withErrorHandling } = useFormErrorHandler(setError, clearErrors);
  
  const [login, { isLoading }] = useLoginMutation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const nextPath = sanitizeNextPath(searchParams.get("next"));
  const postAuthRedirectPath = getPostAuthRedirectPath(nextPath);

  const onSubmit = async (data: LoginFormData) => {
    await withErrorHandling(async () => {
      await login(data).unwrap();
      navigate(postAuthRedirectPath);
    });
  };

  const isFormLoading = isLoading || isSubmitting;

  return (
    <Box
      minH="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      bg="gray.50"
      _dark={{ bg: "gray.900" }}
      px={4}
    >
      <Box pos="absolute" top="4" right="4">
        <ClientOnly fallback={<Skeleton w="10" h="10" rounded="md" />}>
          <ColorModeToggle />
        </ClientOnly>
      </Box>

      <Card.Root maxW="md" w="full" p={8} shadow="lg">
        <Card.Body>
          <VStack gap={6}>
            <Box textAlign="center">
              <Logo />
              <Heading size="4xl" mt={4} mb={2}>
                Admin Dashboard
              </Heading>
              <Text color="gray.600" _dark={{ color: "gray.400" }}>
                Sign in to your account
              </Text>
            </Box>

            <form onSubmit={handleSubmit(onSubmit)} style={{ width: "100%" }}>
              <VStack gap={4}>
                {/* Display root/general errors using component */}
                <RootErrorDisplay error={errors.root} />
                
                <Box w="full">
                  <Text fontSize="sm" mb={2} fontWeight="medium">
                    Email
                  </Text>
                  <Controller
                    name="email"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="email"
                        placeholder="Enter your email"
                        size="lg"
                        borderColor={errors.email ? "red.500" : undefined}
                      />
                    )}
                  />
                  <FormErrorDisplay error={errors.email} />
                </Box>

                <Box w="full">
                  <Text fontSize="sm" mb={2} fontWeight="medium">
                    Password
                  </Text>
                  <Controller
                    name="password"
                    control={control}
                    render={({ field }) => (
                      <PasswordInput
                        {...field}
                        type="password"
                        placeholder="Enter your password"
                        size="lg"
                        borderColor={errors.password ? "red.500" : undefined}
                      />
                    )}
                  />
                  <FormErrorDisplay error={errors.password} />
                </Box>

                <Button
                  type="submit"
                  colorPalette="blue"
                  size="lg"
                  width="full"
                  loading={isFormLoading}
                  disabled={isFormLoading}
                >
                  Sign In
                </Button>
              </VStack>
            </form>
            <SocialAuthOptions nextPath={nextPath} />
          </VStack>
        </Card.Body>
      </Card.Root>
    </Box>
  );
}
